import { useRef, ReactNode } from 'react';
import { IFeatureProps } from 'mod/featureV2/interface';
import { IBanner } from 'mod/featureV2/interface/banner';
import { isPhone } from 'base/utils/client';
import { merge } from 'ava-util/dist/data';
import { Banner } from 'mod/featureV2/Banner';
import { getBannerTrackData } from 'mod/featureV2/Banner/utils';
import { IBannerProps } from 'mod/featureV2/Banner/interface';
import { isVideoPlaying } from 'base/utils/media';
import { IThemeSwiperRef, ThemeSwiper } from '@base/lib/ThemeSwiper';
import { ITrackElementData, trackElement, TTrackElementAction } from 'base/utils/tracker';
import { IFeatureCarouselBanner } from 'mod/featureV2/interface/carouselBanner';
import { theme } from 'pro/config/theme';
import { getModuleCode } from 'mod/featureV2/utils';
import { isVideoUrl } from 'base/utils/file';
import cn from 'classnames';
import { TTrackerTrigger } from '@base/lib/Tracker';

export type IProps<T extends IBanner = IBanner> = IFeatureProps<IFeatureCarouselBanner<T>> & {
    containerWidth?: number;
    renderBanner?: (bannerProps: IBannerProps<T>, index: number) => ReactNode;
    trackerTrigger?: TTrackerTrigger;
};

function getContainerWidth(fullScreen: boolean, sideSpace: boolean): number {
    const fullWidth = fullScreen ? (isPhone ? 375 : 1920) : theme.wrapperWidth;
    const sideWidth = sideSpace ? theme.siteSpace * 2 : 0;
    return fullWidth - sideWidth;
}

export function CarouselBanner<T extends IBanner>(props: IProps<T>): React.ReactElement {
    const swiperRef = useRef<IThemeSwiperRef>(null);

    const {
        feature,
        renderBanner,
        trackerTrigger = 'impr click',
        containerWidth: proppContainerWidth,
        className,
    } = props;

    const {
        moduleId,
        config,
        list
    } = feature;

    const {
        width = 1920,
        height = 700,
        radius,
        fullScreen = true,
        sideSpace = false,
        largeScreenHeightFixed: configLargeScreenHeightFixed = false,
        bgColor = '#000',
        threshold,
        playThreshold,
        carousel,
        preload = false,
    } = config;

    const {
        navigation = !isPhone,
        autoPlay: configAutoPlay,
        delay = 5000,
        effect,
        slidesPerView = 1,
        spaceBetween = 0,
        pagination,
        onSlideChangeTransitionStart,
        onSlideChangeTransitionEnd,
        onTransitionEnd,
        ...carouselRest
    } = carousel || {};

    const containerWidth = proppContainerWidth || getContainerWidth(fullScreen, sideSpace);
    const bannerWidth = Math.round(((containerWidth + spaceBetween) / slidesPerView) - spaceBetween);
    const bannerHeight = Math.round((bannerWidth * Number(height)) / Number(width));
    const moduleCode = getModuleCode(moduleId);
    const largeScreenHeightFixed = !isPhone && configLargeScreenHeightFixed && fullScreen;
    const autoPlay = typeof configAutoPlay !== 'undefined' ? configAutoPlay : slidesPerView === 1;

    const handleAutoplay = (autoplay: boolean) => {
        if (autoplay) {
            swiperRef.current?.startAutoplay();
        } else {
            swiperRef.current?.stopAutoplay();
        }
    };

    const trackSlide = (action: TTrackElementAction, index: number) => {
        trackElement({
            action,
            ...getBannerTrackData(moduleId, list[index], index)
        } as ITrackElementData);
    };

    return (
        <ThemeSwiper
            className={cn(className, 'g-banner-list')}
            ref={swiperRef}
            loop={slidesPerView === 1}
            pagination={typeof pagination !== 'undefined'
                ? pagination
                : (slidesPerView === 1)
            }
            scrollbar={isPhone && slidesPerView > 1}
            navigation={navigation}
            effect={effect || (isPhone || slidesPerView > 1 ? void 0 : 'fade')}
            autoplay={!autoPlay ? false : {
                delay,
                disableOnInteraction: false,
            }}
            slidesPerView={slidesPerView}
            spaceBetween={spaceBetween}
            list={list}
            renderSlideProps={(item: T, index) => {
                const slideProps: React.HTMLAttributes<HTMLDivElement> = {
                    className: 'g-banner-item'
                }
                if (moduleCode) {
                    slideProps['data-t-tar'] = item.src;
                    slideProps['data-t-pos'] = index + 1;
                    if (trackerTrigger.includes('click')) {
                        slideProps['onClick'] = () => {
                            trackSlide('click', index);
                        }
                    }
                }
                return slideProps;
            }}
            render={(item: T, index) => {
                const videoProps = autoPlay && isVideoUrl(item.src) ? {
                    onPlay: () => handleAutoplay(false),
                    onPause: () => handleAutoplay(true),
                    onEnd: () => handleAutoplay(true),
                } : {};

                const bannerProps: IBannerProps<T> = {
                    banner: merge({ bgColor } as Partial<T>, {
                        ...item,
                        ...videoProps
                    }),
                    width: bannerWidth,
                    height: bannerHeight,
                    radius,
                    largeScreenHeightFixed,
                    threshold,
                    playThreshold,
                    preload: preload && index < slidesPerView,
                    fetchPriority: index >= slidesPerView ? 'low' : void 0
                };

                return typeof renderBanner === 'function' ? renderBanner(bannerProps, index) : <Banner {...bannerProps}/>
            }}
            onInit={() => trackSlide('impr', 0)}
            onTransitionEnd={(swiper) => {
                if (!swiper) {
                    return;
                }
                // 视频停止播放
                swiper.slides.forEach(slide => {
                    const video = slide.querySelector('video') as HTMLVideoElement;
                    if (video && isVideoPlaying(video)) {
                        video.pause();
                    }
                });

                // ctr 埋点
                let index: number = swiper.realIndex;
                if (moduleCode && trackerTrigger.includes('impr')) {
                    trackSlide('impr', index);
                }

                onTransitionEnd?.(swiper);
            }}
            {...carouselRest}
        />
    )
}